import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faEllipsisH,
} from "@fortawesome/free-solid-svg-icons";
import { useNotificationQueryContext } from "../../context/NotificationQueryContext";
import { useNotifications } from "../../hooks/notifications/useNotifications";

/**
 * NotificationPagination Component
 *
 * Provides pagination controls for notification list
 * Shows page numbers, navigation buttons, and page info
 */
const NotificationPagination = ({ className = "" }) => {
  const { queryParams, setPage } = useNotificationQueryContext();
  const { data: notificationsData, isLoading } = useNotifications(queryParams);

  const pagination = notificationsData?.pagination || {};
  const {
    page = 1,
    totalPages = 1,
    total = 0,
    limit = 20,
    hasPrev = false,
    hasNext = false,
  } = pagination;

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);

      if (page > 4) {
        pages.push("...");
      }

      // Show pages around current page
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (page < totalPages - 3) {
        pages.push("...");
      }

      // Show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Don't render if no pagination needed
  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = getPageNumbers();
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className={`notification-pagination ${className}`}>
      <div className="flex items-center justify-between">
        {/* Page info */}
        <div className="text-sm text-gray-700">
          Showing <span className="font-medium">{startItem}</span> to{" "}
          <span className="font-medium">{endItem}</span> of{" "}
          <span className="font-medium">{total}</span> notifications
        </div>

        {/* Pagination controls */}
        <div className="flex items-center space-x-2">
          {/* Previous button */}
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={!hasPrev || isLoading}
            className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          {/* Page numbers */}
          <div className="flex items-center">
            {pageNumbers.map((pageNum, index) => (
              <button
                key={index}
                onClick={() =>
                  typeof pageNum === "number" && handlePageChange(pageNum)
                }
                disabled={typeof pageNum !== "number" || isLoading}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${
                  pageNum === page
                    ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                    : typeof pageNum === "number"
                    ? "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    : "bg-white border-gray-300 text-gray-400 cursor-default"
                } focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
                aria-label={
                  typeof pageNum === "number"
                    ? `Go to page ${pageNum}`
                    : "More pages"
                }
                aria-current={pageNum === page ? "page" : undefined}
              >
                {typeof pageNum === "number" ? (
                  pageNum
                ) : (
                  <FontAwesomeIcon icon={faEllipsisH} />
                )}
              </button>
            ))}
          </div>

          {/* Next button */}
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={!hasNext || isLoading}
            className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="mt-3 flex items-center justify-center">
          <div className="inline-flex items-center text-sm text-gray-500">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mr-2" />
            Loading notifications...
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPagination;
